package com.playoffpulse.frontend.analytics

import com.playoffpulse.frontend.data.FrontendRuntimeFlags
import com.playoffpulse.shared.model.AnalyticsEventName
import com.playoffpulse.shared.model.AnalyticsEventRequest
import com.playoffpulse.shared.model.AnalyticsFailureSource
import com.playoffpulse.shared.model.AnalyticsPageKind
import com.playoffpulse.shared.model.ImportantGamesWindow
import com.playoffpulse.shared.model.LeagueId
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.json.Json

interface AnalyticsClient {
    suspend fun track(event: AnalyticsEventRequest)
}

class BackendAnalyticsClient(
    baseUrl: String = FrontendRuntimeFlags.backendBaseUrl(),
    private val json: Json = Json { ignoreUnknownKeys = true },
) : AnalyticsClient {
    private val normalizedBaseUrl = baseUrl.trimEnd('/')

    override suspend fun track(event: AnalyticsEventRequest) {
        val payload = json.encodeToString(AnalyticsEventRequest.serializer(), event)
        val response = window.fetch(
            "$normalizedBaseUrl/analytics/events",
            js(
                """({
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Accept": "application/json"
                    },
                    body: payload,
                    keepalive: true
                })""",
            ),
        ).await()

        if (!response.ok) {
            error("Analytics request failed (${response.status})")
        }
    }
}

class PlayoffAnalytics(
    private val client: AnalyticsClient,
) {
    suspend fun routeView(
        pageKind: AnalyticsPageKind,
        teamId: String? = null,
        attributedTeamId: String? = null,
    ) = track(
        AnalyticsEventRequest(
            name = AnalyticsEventName.ROUTE_VIEW,
            league = LeagueId.NHL,
            teamId = teamId?.uppercase(),
            attributedTeamId = attributedTeamId?.uppercase(),
            pageKind = pageKind,
        ),
    )

    suspend fun shareUrlCopied(
        pageKind: AnalyticsPageKind? = null,
        teamId: String? = null,
    ) = track(
        AnalyticsEventRequest(
            name = AnalyticsEventName.SHARE_URL_COPIED,
            league = LeagueId.NHL,
            teamId = teamId?.uppercase(),
            pageKind = pageKind,
        ),
    )

    suspend fun initialTeamLoaded(teamId: String) = track(
        AnalyticsEventRequest(
            name = AnalyticsEventName.INITIAL_TEAM_LOADED,
            league = LeagueId.NHL,
            teamId = teamId.uppercase(),
        ),
    )

    suspend fun teamSelected(teamId: String) = track(
        AnalyticsEventRequest(
            name = AnalyticsEventName.TEAM_SELECTED,
            league = LeagueId.NHL,
            teamId = teamId.uppercase(),
        ),
    )

    suspend fun importantGamesTabViewed(
        teamId: String?,
        window: ImportantGamesWindow,
    ) = track(
        AnalyticsEventRequest(
            name = AnalyticsEventName.IMPORTANT_GAMES_TAB_VIEWED,
            league = LeagueId.NHL,
            teamId = teamId?.uppercase(),
            window = window,
        ),
    )

    suspend fun loadFailed(
        source: AnalyticsFailureSource,
        teamId: String? = null,
    ) = track(
        AnalyticsEventRequest(
            name = AnalyticsEventName.LOAD_FAILED,
            league = LeagueId.NHL,
            teamId = teamId?.uppercase(),
            failureSource = source,
        ),
    )

    suspend fun staleSnapshotSeen(teamId: String) = track(
        AnalyticsEventRequest(
            name = AnalyticsEventName.STALE_SNAPSHOT_SEEN,
            league = LeagueId.NHL,
            teamId = teamId.uppercase(),
        ),
    )

    private suspend fun track(event: AnalyticsEventRequest) {
        runCatching { client.track(event) }
    }
}

fun defaultPlayoffAnalytics(): PlayoffAnalytics = PlayoffAnalytics(BackendAnalyticsClient())

internal fun analyticsFailureSource(message: String): AnalyticsFailureSource {
    return when {
        message.startsWith("Could not load standings", ignoreCase = true) -> AnalyticsFailureSource.STANDINGS
        message.startsWith("Could not load a summary", ignoreCase = true) -> AnalyticsFailureSource.TEAM_LOAD
        message.startsWith("Important games unavailable", ignoreCase = true) -> AnalyticsFailureSource.IMPORTANT_GAMES
        message.startsWith("Refresh failed", ignoreCase = true) -> AnalyticsFailureSource.REFRESH
        message.contains("cached snapshot", ignoreCase = true) -> AnalyticsFailureSource.REFRESH
        else -> AnalyticsFailureSource.UNKNOWN
    }
}
